import { Euler, PerspectiveCamera, Vector3 } from 'three';
import type { Time } from '@/core/time';
import { PLAYER } from '@/config';
import { damp, lerp } from '@/utils/math';

/**
 * First-person camera rig. The controller owns position and orientation; the rig
 * owns *feel* — the small, deniable motions that make a body present in the world:
 * a walking bob, a soft dip on landing, and a gentle FOV widening at speed. Kept
 * separate from the controller so the movement math stays pure and the game-feel
 * can be tuned (or disabled for accessibility) independently.
 */
const _eye = new Vector3();
const _euler = new Euler(0, 0, 0, 'YXZ');

export class CameraRig {
  private bobPhase = 0;
  private bobAmount = 0;
  private landOffset = 0;
  private landVel = 0;
  private wasGrounded = true;
  private prevY = 0;
  private fov: number;
  private baseFov: number;

  constructor(private camera: PerspectiveCamera) {
    this.baseFov = camera.fov;
    this.fov = camera.fov;
  }

  update(
    feet: Vector3,
    yaw: number,
    pitch: number,
    speed: number,
    moving: boolean,
    grounded: boolean,
    time: Time,
  ): void {
    const dt = time.delta;

    // Head bob: phase driven by distance travelled, amplitude by speed.
    if (moving) {
      this.bobPhase += speed * dt * 1.7;
      this.bobAmount = damp(this.bobAmount, Math.min(1, speed / PLAYER.sprintSpeed), 8, dt);
    } else {
      this.bobAmount = damp(this.bobAmount, 0, 8, dt);
    }
    const bobY = Math.sin(this.bobPhase * 2) * 0.045 * this.bobAmount;
    const bobX = Math.cos(this.bobPhase) * 0.035 * this.bobAmount;

    // Landing dip: detect the touchdown edge and inject a downward spring impulse.
    if (grounded && !this.wasGrounded) {
      const impact = Math.min(1, Math.abs(feet.y - this.prevY) * 6 + 0.3);
      this.landVel -= impact * 3.2;
    }
    // Critically-damped spring back to zero.
    const k = 90;
    const c = 15;
    this.landVel += (-k * this.landOffset - c * this.landVel) * dt;
    this.landOffset += this.landVel * dt;
    this.wasGrounded = grounded;
    this.prevY = feet.y;

    // Compose eye position.
    _eye.copy(feet);
    _eye.y += PLAYER.eyeHeight + bobY + this.landOffset;

    // Small lateral bob applied in view space (via right vector).
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    _eye.x += rightX * bobX;
    _eye.z += rightZ * bobX;

    this.camera.position.copy(_eye);

    // Orientation.
    _euler.set(pitch, yaw, bobX * 0.4, 'YXZ');
    this.camera.quaternion.setFromEuler(_euler);

    // FOV widens with speed for a subtle sense of momentum.
    const targetFov = this.baseFov + lerp(0, 7, Math.min(1, speed / PLAYER.sprintSpeed));
    this.fov = damp(this.fov, targetFov, 6, dt);
    if (Math.abs(this.fov - this.camera.fov) > 0.01) {
      this.camera.fov = this.fov;
      this.camera.updateProjectionMatrix();
    }
  }
}
